/**
 * Hook para gerenciamento de tabelas de preços CRM
 * Fornece queries e mutations para CRUD de tabelas de preços e seus itens
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { CrmProduct } from './useCrmProducts';

// Tipo para tabela de preços
// NOTA: workspace_id foi removido em favor de relação many-to-many via crm_price_table_workspaces
export interface CrmPriceTable {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  currency: string;
  conditions: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Workspaces vinculados (via tabela de junção) */
  workspaces?: Array<{
    id: string;
    name: string;
  }>;
  items_count?: number;
}

// Tipo para item de tabela de preços
export interface CrmPriceTableItem {
  id: string;
  tenant_id: string;
  price_table_id: string;
  product_id: string;
  price: number;
  min_quantity: number;
  discount_percent: number;
  discount_fixed: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  product?: CrmProduct;
}

// Tipo para criação de tabela de preços
// NOTA: workspace_ids é gerenciado separadamente via useCrmItemWorkspaces
export interface CreatePriceTableInput {
  name: string;
  description?: string;
  is_default?: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
  currency?: string;
}

// Tipo para atualização de tabela de preços
// NOTA: workspace_ids é gerenciado separadamente via useCrmItemWorkspaces
export interface UpdatePriceTableInput {
  id: string;
  name?: string;
  description?: string | null;
  is_default?: boolean;
  is_active?: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
  currency?: string;
}

// Tipo para criação de item de tabela
export interface CreatePriceTableItemInput {
  price_table_id: string;
  product_id: string;
  price: number;
  min_quantity?: number;
  discount_percent?: number;
  discount_fixed?: number;
}

// Tipo para atualização de item de tabela
export interface UpdatePriceTableItemInput {
  id: string;
  price?: number;
  min_quantity?: number;
  discount_percent?: number;
  discount_fixed?: number;
  is_active?: boolean;
}

// Opções para configurar o hook
export interface CrmPriceTablesOptions {
  /** Filtrar por workspace (via tabela de junção crm_price_table_workspaces) */
  workspaceId?: string | null;
}

/**
 * Hook principal para tabelas de preços CRM
 */
export function useCrmPriceTables(options?: CrmPriceTablesOptions) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  // Query para listar tabelas de preços com workspaces vinculados
  const priceTablesQuery = useQuery({
    queryKey: ['crm-price-tables', currentTenant?.id, options?.workspaceId],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // Se filtro de workspace ativo, buscar IDs das tabelas vinculadas primeiro
      let workspaceTableIds: string[] | null = null;
      if (options?.workspaceId) {
        const { data: workspaceLinks, error: wsError } = await supabase
          .from('crm_price_table_workspaces')
          .select('price_table_id')
          .eq('workspace_id', options.workspaceId);
        
        if (wsError) throw wsError;
        
        // Se não há tabelas vinculadas ao workspace, retornar array vazio
        if (!workspaceLinks || workspaceLinks.length === 0) {
          return [];
        }
        workspaceTableIds = workspaceLinks.map(l => l.price_table_id);
      }

      // Buscar tabelas de preço
      let tablesQuery = supabase
        .from('crm_price_tables')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .order('is_default', { ascending: false })
        .order('name', { ascending: true });

      // Filtrar por IDs do workspace (se aplicável)
      if (workspaceTableIds) {
        tablesQuery = tablesQuery.in('id', workspaceTableIds);
      }

      const { data: tables, error: tablesError } = await tablesQuery;

      if (tablesError) throw tablesError;
      if (!tables) return [];

      // Buscar workspaces vinculados para cada tabela
      const tableIds = tables.map((t) => t.id);
      const { data: workspaceLinks, error: linksError } = await supabase
        .from('crm_price_table_workspaces')
        .select('price_table_id, workspace:crm_workspaces(id, name)')
        .in('price_table_id', tableIds);

      if (linksError) throw linksError;

      // Agrupar workspaces por tabela
      const workspacesByTable: Record<string, Array<{ id: string; name: string }>> = {};
      workspaceLinks?.forEach((link) => {
        if (!workspacesByTable[link.price_table_id]) {
          workspacesByTable[link.price_table_id] = [];
        }
        if (link.workspace) {
          // workspace pode ser array devido ao join, pegar primeiro item
          const ws = Array.isArray(link.workspace) ? link.workspace[0] : link.workspace;
          if (ws) {
            workspacesByTable[link.price_table_id].push(ws);
          }
        }
      });

      // Combinar dados
      return tables.map((table) => ({
        ...table,
        workspaces: workspacesByTable[table.id] || [],
      })) as CrmPriceTable[];
    },
    enabled: !!currentTenant?.id,
  });

  // Mutation para criar tabela de preços
  // NOTA: Após criar, usar useCrmItemWorkspaces para vincular workspaces
  const createPriceTable = useMutation({
    mutationFn: async (input: CreatePriceTableInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      const { data, error } = await supabase
        .from('crm_price_tables')
        .insert({
          tenant_id: currentTenant.id,
          name: input.name,
          description: input.description || null,
          is_default: input.is_default || false,
          valid_from: input.valid_from || null,
          valid_until: input.valid_until || null,
          currency: input.currency || 'BRL',
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-price-tables'] });
      toast.success('Tabela de preços criada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar tabela de preços: ${error.message}`);
    },
  });

  // Mutation para atualizar tabela de preços
  const updatePriceTable = useMutation({
    mutationFn: async (input: UpdatePriceTableInput) => {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('crm_price_tables')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-price-tables'] });
      toast.success('Tabela de preços atualizada com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar tabela de preços: ${error.message}`);
    },
  });

  // Mutation para deletar tabela de preços
  const deletePriceTable = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_price_tables')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-price-tables'] });
      toast.success('Tabela de preços excluída com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir tabela de preços: ${error.message}`);
    },
  });

  return {
    priceTables: priceTablesQuery.data || [],
    isLoading: priceTablesQuery.isLoading,
    error: priceTablesQuery.error,
    createPriceTable,
    updatePriceTable,
    deletePriceTable,
    refetch: priceTablesQuery.refetch,
  };
}

/**
 * Hook para itens de uma tabela de preços específica
 */
export function useCrmPriceTableItems(priceTableId: string | null) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Query para listar itens de uma tabela de preços
  const itemsQuery = useQuery({
    queryKey: ['crm-price-table-items', priceTableId],
    queryFn: async () => {
      if (!priceTableId) return [];

      const { data, error } = await supabase
        .from('crm_price_table_items')
        .select(`
          *,
          product:crm_products(id, name, sku, default_price, unit_type, product_type)
        `)
        .eq('price_table_id', priceTableId)
        .order('min_quantity', { ascending: true });

      if (error) throw error;
      return data as CrmPriceTableItem[];
    },
    enabled: !!priceTableId,
  });

  // Mutation para criar item
  const createItem = useMutation({
    mutationFn: async (input: CreatePriceTableItemInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      const { data, error } = await supabase
        .from('crm_price_table_items')
        .insert({
          tenant_id: currentTenant.id,
          price_table_id: input.price_table_id,
          product_id: input.product_id,
          price: input.price,
          min_quantity: input.min_quantity || 1,
          discount_percent: input.discount_percent || 0,
          discount_fixed: input.discount_fixed || 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-price-table-items'] });
      toast.success('Item adicionado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao adicionar item: ${error.message}`);
    },
  });

  // Mutation para atualizar item
  const updateItem = useMutation({
    mutationFn: async (input: UpdatePriceTableItemInput) => {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('crm_price_table_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-price-table-items'] });
      toast.success('Item atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar item: ${error.message}`);
    },
  });

  // Mutation para deletar item
  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_price_table_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-price-table-items'] });
      toast.success('Item removido com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover item: ${error.message}`);
    },
  });

  return {
    items: itemsQuery.data || [],
    isLoading: itemsQuery.isLoading,
    error: itemsQuery.error,
    createItem,
    updateItem,
    deleteItem,
    refetch: itemsQuery.refetch,
  };
}
